import { useEffect, useState } from "react";
import {
  createJiraIssue,
  getJiraInstallation,
  JiraApiError,
  type CreatedJiraIssue,
  type JiraInstallationView,
  type RunHealDetail,
} from "../lib/api";

/**
 * Modal for filing a heal as a Jira issue. Mirrors HealLinearDialog but
 * shape differs slightly: Jira uses (project, summary, description) plus
 * issue type. We default the type to whatever the connection saved
 * (typically "Task").
 */
export function HealJiraDialog(props: {
  heal: RunHealDetail;
  onClose: () => void;
}): JSX.Element {
  const { heal, onClose } = props;

  const [installation, setInstallation] = useState<JiraInstallationView | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);
  const [projectKey, setProjectKey] = useState<string>("");
  const [summary, setSummary] = useState<string>(defaultIssueTitle(heal));
  const [description, setDescription] = useState<string>(defaultIssueBody(heal));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CreatedJiraIssue | null>(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await getJiraInstallation();
        if (cancelled) return;
        setInstallation(inst);
        if (inst?.defaultProjectKey) setProjectKey(inst.defaultProjectKey);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof JiraApiError && err.code === "INTEGRATION_NOT_ALLOWED") {
          setNeedsUpgrade(true);
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!summary.trim()) {
      setError("Summary is required.");
      return;
    }
    setBusy(true);
    try {
      const issue = await createJiraIssue({
        summary: summary.trim(),
        description,
        ...(projectKey ? { projectKey } : {}),
      });
      setSuccess(issue);
    } catch (err) {
      if (err instanceof JiraApiError) {
        if (err.code === "INTEGRATION_NOT_ALLOWED") setNeedsUpgrade(true);
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send as Jira issue"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100">
            Send as Jira issue
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && (
          <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
            {error}
            {needsUpgrade && (
              <>
                {" "}
                <a className="underline" href="/account#plan">
                  Upgrade to Pro
                </a>
                .
              </>
            )}
          </div>
        )}

        {success ? (
          <div className="rounded-md border border-emerald-700/60 bg-emerald-900/30 p-4 text-sm text-emerald-100">
            <p className="font-medium">
              Created{" "}
              <a
                href={success.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {success.key}
              </a>
            </p>
            <p className="mt-1 text-emerald-300/80">{success.summary}</p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-emerald-700 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-900/50"
              >
                Done
              </button>
              <a
                href={success.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500"
              >
                Open in Jira →
              </a>
            </div>
          </div>
        ) : !loaded ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !installation ? (
          <div className="rounded-md border border-slate-700 bg-slate-800/40 p-4 text-sm text-slate-300">
            Jira isn't connected on this account yet.{" "}
            <a className="underline" href="/account#jira">
              Connect Jira
            </a>{" "}
            to file heal proposals as issues.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Site
              </label>
              <p className="mt-1 text-sm text-slate-200">
                {installation.siteName ?? installation.siteUrl}
              </p>
            </div>

            {installation.projects && installation.projects.length > 0 ? (
              <div>
                <label className="block text-xs font-medium text-slate-300">
                  Project
                </label>
                <select
                  value={projectKey}
                  onChange={(e) => setProjectKey(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  {!installation.defaultProjectKey && (
                    <option value="">Pick a project…</option>
                  )}
                  {installation.projects.map((p) => (
                    <option key={p.id} value={p.key}>
                      {p.name} ({p.key})
                    </option>
                  ))}
                </select>
              </div>
            ) : installation.defaultProjectKey ? (
              <p className="text-xs text-slate-400">
                Default project:{" "}
                <span className="text-slate-300">
                  {installation.defaultProjectName ?? "(unnamed)"} (
                  {installation.defaultProjectKey})
                </span>
              </p>
            ) : (
              <p className="text-xs text-rose-300">
                No project picker — open the Account page and{" "}
                <a className="underline" href="/account#jira">
                  refresh projects
                </a>{" "}
                first.
              </p>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-300">
                Summary
              </label>
              <input
                type="text"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300">
                Description (plain text — Jira renders as a paragraph)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100"
              />
            </div>

            <div className="mt-1 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  busy ||
                  !summary.trim() ||
                  Boolean(
                    installation.projects &&
                      installation.projects.length > 0 &&
                      !projectKey,
                  )
                }
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Filing…" : "Create issue"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function healCrawlId(heal: RunHealDetail): string {
  return heal.newCrawlId ?? heal.previousCrawlId ?? "(unknown crawl)";
}

function defaultIssueTitle(heal: RunHealDetail): string {
  const n = heal.totalCount;
  const noun = n === 1 ? "selector change" : "selector changes";
  return `Heal: ${n} ${noun} on ${healCrawlId(heal)}`;
}

function defaultIssueBody(heal: RunHealDetail): string {
  const lines = [
    `UAIP heal proposal — generated by self-healing.`,
    ``,
    `Crawl: ${healCrawlId(heal)}`,
    heal.runId ? `Run: ${heal.runId}` : "",
    `Source: ${heal.source}`,
    `Status: ${heal.status}`,
    `Decisions made: ${heal.decisions.length} of ${heal.totalCount}`,
    ``,
    `Open in UAIP: ${typeof window !== "undefined" ? window.location.origin : ""}/heals/${encodeURIComponent(heal.id)}`,
  ].filter(Boolean);
  return lines.join("\n");
}
